/* import { useState, type ComponentType } from "react"; */
import { useEffect, useState, type ComponentType } from "react";
import {
  Construction,
  Anchor,
  PackageSearch,
  Truck,
  Ship,
  Mountain,
  Microscope,
  MoveDown,
  Hammer,
  Boxes,
  Wrench,
  ShipWheel,
  Settings2,
  Cog,
  ChevronDown,
} from "lucide-react";
import { SectionHeader } from "./Section";

type Service = {
  icon: ComponentType<{ className?: string }>;
  title: string;
  summary: string;
  items: string[];
};

const SERVICES: Service[] = [
  {
    icon: Construction,
    title: "Crane Inspection & Maintenance",
    summary: "Full lifecycle inspection and maintenance for onshore and offshore cranes.",
    items: [
      "Onshore Cranes",
      "Offshore Cranes",
      "API Annual Inspection",
      "Five-Year Inspection",
      "Re-Certification",
      "Load Testing",
      "Certification",
    ],
  },
  {
    icon: PackageSearch,
    title: "Lifting Equipment Inspection",
    summary: "Statutory inspection and certification of all lifting equipment categories.",
    items: ["Cranes", "Winches", "Chain Blocks", "Forklifts", "Storage Retrieval Systems"],
  },
  {
    icon: Boxes,
    title: "Lifting Gear Inspection",
    summary: "Thorough examination of loose lifting gear in accordance with LEEA standards.",
    items: [
      "Slings",
      "Shackles",
      "Hooks",
      "Lifting Beams",
      "Spreaders",
      "Eyebolts",
      "Lifting Points",
       "Others"
    ],
  },
  {
    icon: Anchor,
    title: "Offshore Inspection Solutions",
    summary: "Specialised offshore equipment inspection by certified offshore inspectors.",
    items: [
      "Pedestal Cranes",
      "BOP Monorail Hoists",
      "Super Sack Hoists",
      "Articulating Boom Cranes",
      "Deck Mounted Cranes",
      "Man Baskets",
      "Spreader Beams",
      "Others",
    ],
  },
  {
    icon: Truck,
    title: "Earth Moving Equipment Inspection",
    summary: "Pre-use and periodic inspection of heavy plant and construction machinery.",
    items: [
      "Graders",
      "Bulldozers",
      "Excavators",
      "Compactors",
      "Dump Trucks",
      "Loaders",
      "Scrapers",
      "Generators",
      "Others",
    ],
  },
  {
    icon: Microscope,
    title: "Non Destructive Testing (NDT)",
    summary: "ASNT Level II & III certified NDT services across multiple disciplines.",
    items: [
      "Visual Testing (VT)",
      "Ultrasonic Testing (UT)",
      "Magnetic Particle (MT)",
      "Liquid Penetrant (PT)",
      "Eddy Current (ET)",
    ],
  },
  {
    icon: MoveDown,
    title: "Rope Access Inspection",
    summary:
      "IRATA-aligned rope access for inspection of structures at height or in confined spaces.",
    items: [
      "Structural Surveys",
      "Weld Inspection at Height",
      "Confined Space Access",
      "Painting & Coating Surveys",
    ],
  },
  {
    icon: Mountain,
    title: "DROPS Inspection Services",
    summary: "Dropped Object Prevention Surveys safeguarding offshore and onshore personnel.",
    items: [
      "Derrick DROPS Surveys",
      "Fixed Equipment Surveys",
      "Risk Register Development",
      "Mitigation Engineering",
    ],
  },
  {
    icon: Hammer,
    title: "Load Testing Services",
    summary: "Proof load testing under controlled, documented and witnessed conditions.",
    items: [
      "Offshore Load Testing",
      "Water Bag Testing",
      "Onshore Load Testing",
      "Dead Weight Testing",
    ],
  },
  {
    icon: Settings2,
    title: "API Category III & IV Inspections",
    summary: "API-compliant inspection of offshore and onshore equipment.",
    items: [
      "Category III Inspection",
      "Category IV Inspection",
      "Repair Recommendations",
      "Re-Certification",
    ],
  },
  {
    icon: Wrench,
    title: "Handling Tools Inspection",
    summary: "Inspection of drilling and well-services handling tools to API standards.",
    items: ["Elevators", "Slips", "Tongs", "Spiders", "Drill Pipe Handling Tools", "Others"],
  },
  {
    icon: Cog,
    title: "Derrick, Mast & Substructure Inspection",
    summary: "Structural integrity inspection of derricks, masts and rig substructures.",
    items: [
      "NDT of Critical Welds",
      "Bolted Connections",
      "Substructure Inspection",
      "Crown Block Inspection",
      "Travelling Block",
      "Top Drive System",
      "Deadline Anchor",
      "Draw Works",
      "Others",
    ],
  },
  /* {
    icon: Ship,
    title: "Offshore Marine Services",
    summary: "End-to-end marine support for offshore lifting and handling operations.",
    items: [
      "Cargo Securing Surveys",
      "Sea-Fastening Inspection",
      "Marine Warranty Support",
      "Vessel Lifting Audits",
    ],
  }, */
  {
    icon: ShipWheel,
    title: "Overhauling & Refurbishment",
    summary: "Workshop overhaul, refurbishment and life-extension of critical equipment.",
    items: ["Crane Overhauls", "Winch Refurbishment", "Hoist Rebuilds", "Component Renewal"],
  },
  {
    icon: Boxes,
    title: "CCU Inspection & Fabrication",
    summary: "DNV 2.7-1 / 2.7-3 cargo carrying unit inspection and certified fabrication.",
    items: [
      "DNV 2.7-1 Inspection",
      "Re-Certification",
      "Repair & Refurbishment",
      "New Build Fabrication",
    ],
  },
  {
    icon: PackageSearch,
    title: "Certified Spare Parts Supply",
    summary: "Genuine and certified spare parts for critical lifting and handling equipment.",
    items: [
      "OEM Spare Parts",
      "Wire Ropes",
      "Sheaves & Bearings",
      "Hooks & Blocks",
      "Hydraulic Components",
    ],
  },
];

export function Services() {
  const [open, setOpen] = useState<number | null>(0);
  const openServiceFromHash = () => {
    const hash = decodeURIComponent(window.location.hash.replace("#", ""));

    const index = SERVICES.findIndex(
      (s) =>
        `services-${s.title.replace(/\s+/g, "-").toLowerCase()}` === hash
    );

    if (index !== -1) {
      setOpen(index);

      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  useEffect(() => {
    openServiceFromHash();

    window.addEventListener("hashchange", openServiceFromHash);

    return () => {
      window.removeEventListener("hashchange", openServiceFromHash);
    };
  }, []);

  return (
    <section id="services" className="section-y bg-background">
      <div className="container-x ">
        <SectionHeader
          eyebrow="Services"
          title="A complete inspection and engineering portfolio."
          intro="From crane certification to NDT, rope access, DROPS surveys and full overhauls — Solid Proof delivers a single, accountable partner across the asset lifecycle."
        />

        <div className="mt-14 grid gap-4 lg:grid-cols-2 items-start ">
          {SERVICES.map((s, i) => {
            const isOpen = open === i;
            const id = `services-${s.title.replace(/\s+/g, "-").toLowerCase()}`;
            return (
              <div
                id={id}
                key={s.title}
                className={`overflow-hidden  rounded-xl border bg-card transition-all ${isOpen ? "border-orange shadow-xl" : "border-border hover:border-ocean/40"
                  }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-start gap-4 p-6 text-left"
                  aria-expanded={isOpen}
                >
                  <span
                    className={`grid h-12 w-12 shrink-0 place-items-center rounded-md transition-colors ${isOpen ? "bg-orange text-white" : "bg-navy-deep text-orange"
                      }`}
                  >
                    <s.icon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 flex-1 cursor-pointer">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-lg font-bold text-navy-deep">{s.title}</h3>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180 text-orange" : ""
                          }`}
                      />
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {s.summary}
                    </p>
                  </div>
                </button>
                <div
                  className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-border bg-light-gray px-6 py-5">
                      <div className="text-xs font-bold uppercase tracking-widest text-orange">
                        Capabilities
                      </div>
                      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                        {s.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-navy-deep">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
